import React, { useState } from 'react';
import { Customer } from '@/hooks/useCustomers';

interface Supplier {
  id: string;
  name: string;
  logo: string;
  state: string;
}

interface Portal {
  id: string;
  region: string;
  supplier: string;
  logo: string;
  status: string;
  lastSync: string;
  billsCollected: number;
  credentials?: any;
}

interface PortalSetupModalProps {
  customer: Customer;
  onClose: () => void;
  onPortalAdded: (portal: Portal) => void;
}

const REGIONAL_SUPPLIERS: Record<string, Supplier[]> = {
  USA: [
    { id: 'pge', name: 'Pacific Gas & Electric (PG&E)', logo: '/logos/pge.png', state: 'California' },
    { id: 'sce', name: 'Southern California Edison', logo: '/logos/sce.png', state: 'California' },
    { id: 'con_ed', name: 'Consolidated Edison', logo: '/logos/coned.png', state: 'New York' },
    { id: 'duke', name: 'Duke Energy', logo: '/logos/duke.png', state: 'Multiple States' },
    { id: 'florida_power', name: 'Florida Power & Light', logo: '/logos/fpl.png', state: 'Florida' },
    { id: 'national_grid', name: 'National Grid', logo: '/logos/nationalgrid.png', state: 'Multiple States' },
    { id: 'xcel', name: 'Xcel Energy', logo: '/logos/xcel.png', state: 'Multiple States' }
  ],
  Australia: [
    { id: 'agl', name: 'AGL Energy', logo: '/logos/agl.png', state: 'National' },
    { id: 'origin', name: 'Origin Energy', logo: '/logos/origin.png', state: 'National' },
    { id: 'energy_australia', name: 'EnergyAustralia', logo: '/logos/energyaustralia.png', state: 'National' },
    { id: 'red_energy', name: 'Red Energy', logo: '/logos/red.png', state: 'VIC/NSW/QLD/SA' },
    { id: 'alinta', name: 'Alinta Energy', logo: '/logos/alinta.png', state: 'National' }
  ],
  UK: [
    { id: 'british_gas', name: 'British Gas', logo: '/logos/british_gas.png', state: 'National' },
    { id: 'edf', name: 'EDF Energy', logo: '/logos/edf.png', state: 'National' },
    { id: 'eon', name: 'E.ON Next', logo: '/logos/eon.png', state: 'National' },
    { id: 'ovo', name: 'OVO Energy', logo: '/logos/ovo.png', state: 'National' },
    { id: 'octopus', name: 'Octopus Energy', logo: '/logos/octopus.png', state: 'National' },
    { id: 'scottish_power', name: 'ScottishPower', logo: '/logos/scottishpower.png', state: 'National' }
  ]
};

export const PortalSetupModal: React.FC<PortalSetupModalProps> = ({ customer, onClose, onPortalAdded }) => {
  const [step, setStep] = useState(1); // 1: Region, 2: Supplier, 3: Credentials
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    securityQuestions: '',
    notes: '',
    syncFrequency: 'daily'
  });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSuppliers = selectedRegion 
    ? REGIONAL_SUPPLIERS[selectedRegion].filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.state.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const handleSubmit = async () => {
    if (!selectedSupplier) return;
    
    const newPortal: Portal = {
      id: Date.now().toString(),
      region: selectedRegion,
      supplier: selectedSupplier.name,
      logo: selectedSupplier.logo,
      status: 'active',
      lastSync: 'Just now',
      billsCollected: 0,
      credentials: {
        ...credentials,
        password: '[ENCRYPTED]' // Never store plain text passwords
      }
    };
    
    onPortalAdded(newPortal);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add Portal Integration</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Step {step} of 3: {
                step === 1 ? 'Select Region' :
                step === 2 ? 'Choose Supplier' :
                'Enter Credentials'
              }
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        {/* Step 1: Region Selection */}
        {step === 1 && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Select Region</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.keys(REGIONAL_SUPPLIERS).map(region => (
                <button
                  key={region}
                  onClick={() => {
                    setSelectedRegion(region);
                    setStep(2);
                  }}
                  className={`p-4 border-2 rounded-lg text-center hover:border-primary/30 hover:bg-primary/5 transition-colors ${
                    selectedRegion === region ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="text-2xl mb-2">
                    {region === 'USA' ? '🇺🇸' : region === 'Australia' ? '🇦🇺' : '🇬🇧'}
                  </div>
                  <div className="font-medium text-foreground">{region}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {REGIONAL_SUPPLIERS[region].length} suppliers available
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Supplier Selection */}
        {step === 2 && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Choose Supplier in {selectedRegion}
              </h3>
              <button 
                onClick={() => setStep(1)}
                className="text-primary hover:text-primary/80 text-sm"
              >
                ← Change Region
              </button>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search suppliers..."
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Suppliers Grid */}
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {filteredSuppliers.map(supplier => (
                <button
                  key={supplier.id}
                  onClick={() => {
                    setSelectedSupplier(supplier);
                    setStep(3);
                  }}
                  className={`flex items-center gap-4 p-4 border-2 rounded-lg text-left hover:border-primary/30 hover:bg-primary/5 transition-colors ${
                    selectedSupplier?.id === supplier.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <img 
                    src={supplier.logo} 
                    alt={supplier.name}
                    className="w-12 h-12 rounded object-contain"
                    onError={(e) => {(e.target as HTMLImageElement).src = '/placeholder-logo.png'}}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{supplier.name}</div>
                    <div className="text-sm text-muted-foreground">{supplier.state}</div>
                  </div>
                  <div className="text-primary">→</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Credentials */}
        {step === 3 && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">Enter Credentials</h3>
                <p className="text-sm text-muted-foreground">
                  Connecting to {selectedSupplier?.name} portal
                </p>
              </div>
              <button 
                onClick={() => setStep(2)}
                className="text-primary hover:text-primary/80 text-sm"
              >
                ← Change Supplier
              </button>
            </div>

            {/* Security Notice */}
            <div className="mb-6 p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-green-600 text-sm font-medium mb-1">
                🔒 Security Notice
              </div>
              <p className="text-green-600/80 text-sm">
                Your credentials are encrypted and stored securely. We only use them to access your utility account 
                for bill collection. Your login information is never shared with third parties.
              </p>
            </div>

            {/* Credentials Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Username / Email *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter your portal username or email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="Enter your portal password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Security Questions / 2FA Notes
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  rows={3}
                  value={credentials.securityQuestions}
                  onChange={(e) => setCredentials({...credentials, securityQuestions: e.target.value})}
                  placeholder="Any security questions, 2FA setup, or special login instructions..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Sync Frequency
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  value={credentials.syncFrequency}
                  onChange={(e) => setCredentials({...credentials, syncFrequency: e.target.value})}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Additional Notes
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  rows={2}
                  value={credentials.notes}
                  onChange={(e) => setCredentials({...credentials, notes: e.target.value})}
                  placeholder="Any additional information about this account..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {step === 3 && (
              <span>🔒 All credentials are encrypted before storage</span>
            )}
          </div>
          <div className="flex gap-2">
            <button 
              onClick={onClose}
              className="px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
            >
              Cancel
            </button>
            {step === 3 && (
              <button 
                onClick={handleSubmit}
                disabled={!credentials.username || !credentials.password}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                🔗 Connect Portal
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};